import Link from 'next/link';
import dayjs from 'dayjs';

import { AuthorAvatar } from './AuthorAvatar';
import { ARTICLE_TOKENS } from './design-tokens';
import { buildArticleBreadcrumbs } from '@lib/article-seo';
import { buildAuthorPath } from '@lib/listings/normalise';
import { buildSanityImageSrcSet, buildSanityImageUrl } from '@lib/sanity';
import type { SanityArticlePage } from '@lib/sanity';

const FEATURED_IMAGE_WIDTHS = [640, 960, 1280, 1600];
const FEATURED_IMAGE_SIZES = '(min-width: 48rem) 44rem, 100vw';
const FEATURED_IMAGE_DEFAULT_WIDTH = 1280;
const PUBLISHED_FORMAT = 'D MMM YYYY';
const UPDATED_FORMAT = 'DD/MM/YY';

type ArticleHeaderProps = {
  page: SanityArticlePage;
  /** Omitted on premium pieces: the static page only holds the free half. */
  readMinutes?: number;
};

/** Only shown when an editor pressed Update after the piece was published. */
export const wasUpdatedAfterPublish = (
  publishedAt: string | undefined,
  lastUpdatedAt: string | undefined
): boolean =>
  Boolean(publishedAt && lastUpdatedAt) &&
  dayjs(lastUpdatedAt).isAfter(dayjs(publishedAt), 'day');

export const ArticleHeader = ({ page, readMinutes }: ArticleHeaderProps) => {
  const { featuredImage, author, publishedAt, lastUpdatedAt } = page;
  const [siteSection, ...sectionCrumbs] = buildArticleBreadcrumbs(page);
  const showUpdated = wasUpdatedAfterPublish(publishedAt, lastUpdatedAt);
  const { width, height } = featuredImage?.asset?.metadata?.dimensions ?? {};

  return (
    <header>
      <nav
        aria-label="Breadcrumb"
        className={`mb-5 flex gap-2.5 font-body tabular-nums text-[11.5px] uppercase tracking-[0.08em]`}
      >
        <Link
          href={siteSection.path ?? '/'}
          className={`${ARTICLE_TOKENS.accentText} font-medium`}
        >
          {siteSection.label}
        </Link>
        {sectionCrumbs.map((crumb) => (
          <span key={crumb.label} className="flex gap-2.5">
            <span className={ARTICLE_TOKENS.chrome}>/</span>
            {crumb.path ? (
              <Link
                href={crumb.path}
                className={`${ARTICLE_TOKENS.muted} hover:underline`}
              >
                {crumb.label}
              </Link>
            ) : (
              <span className={ARTICLE_TOKENS.muted}>{crumb.label}</span>
            )}
          </span>
        ))}
      </nav>

      <h1
        className={`mb-5 font-heading text-[2rem] font-extrabold leading-[1.08] tracking-[-0.02em] sm:text-[46px] ${ARTICLE_TOKENS.ink} [text-wrap:pretty]`}
      >
        {page.title}
      </h1>

      {page.standfirst && (
        <p
          className={`mb-7 font-body text-xl leading-[1.5] ${ARTICLE_TOKENS.bodyMuted} [text-wrap:pretty]`}
        >
          {page.standfirst}
        </p>
      )}

      {(author || publishedAt) && (
        <div
          className={`mb-9 flex flex-wrap items-center gap-3.5 border-y py-4 ${ARTICLE_TOKENS.rule}`}
        >
          {author && <AuthorAvatar author={author} />}
          <div className="min-w-0 flex-1">
            {author && (
              <div
                className={`font-heading text-sm font-bold ${ARTICLE_TOKENS.ink}`}
              >
                <Link
                  href={buildAuthorPath(author.slug)}
                  className="hover:underline"
                >
                  {author.name}
                </Link>
              </div>
            )}
            {publishedAt && (
              <div className={`text-[13px] ${ARTICLE_TOKENS.muted}`}>
                Published {dayjs(publishedAt).format(PUBLISHED_FORMAT)}
                {readMinutes !== undefined && ` · ${readMinutes} min read`}
              </div>
            )}
          </div>
          {showUpdated && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-[#cfe6cf] bg-[#eef8ee] px-3 py-[7px] font-body tabular-nums text-[11.5px] tracking-[0.04em] text-[#2e6b34]"
              data-cy="article-updated-pill"
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-[#3d9147]"
              />
              UPDATED {dayjs(lastUpdatedAt).format(UPDATED_FORMAT)}
            </span>
          )}
        </div>
      )}

      {featuredImage?.asset && (
        <figure className="mb-11">
          <div className="aspect-video overflow-hidden rounded-[14px] bg-[#eff2f4]">
            <img
              src={buildSanityImageUrl(featuredImage)
                .width(FEATURED_IMAGE_DEFAULT_WIDTH)
                .url()}
              srcSet={buildSanityImageSrcSet(
                featuredImage,
                FEATURED_IMAGE_WIDTHS
              )}
              sizes={FEATURED_IMAGE_SIZES}
              alt={featuredImage.alt ?? ''}
              width={width}
              height={height}
              className="h-full w-full object-cover"
              fetchPriority="high"
            />
          </div>
          {(featuredImage.caption || featuredImage.credit) && (
            <figcaption
              className={`mt-2.5 text-[13px] ${ARTICLE_TOKENS.mutedLight}`}
            >
              {featuredImage.caption}
              {featuredImage.credit && (
                <span className={ARTICLE_TOKENS.mutedLighter}>
                  {featuredImage.caption ? ' · ' : ''}
                  {featuredImage.credit}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      )}
    </header>
  );
};
